import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiRequest } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

type AddMode = 'password' | 'link';

interface InviteMemberData {
  email: string;
  full_name: string;
  role: 'COMPANY_ADMIN' | 'COMPANY_MEMBER';
  password: string;
  confirmPassword: string;
}

export function InviteMemberModal({ isOpen, onClose, companyId, companyName }: InviteMemberModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<AddMode>('password');
  const [joinUrl, setJoinUrl] = useState('');
  const [formData, setFormData] = useState<InviteMemberData>({
    email: '',
    full_name: '',
    role: 'COMPANY_MEMBER',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inviteMutation = useMutation({
    mutationFn: async (data: InviteMemberData) => {
      const response = await apiRequest('POST', `/api/companies/${companyId}/members`, {
        email: data.email.trim(),
        full_name: data.full_name.trim(),
        role: data.role,
        send_link: mode === 'link',
        password: mode === 'password' ? data.password : undefined,
      });
      return response.json();
    },
    onSuccess: (data: { invite_token?: string; email: string }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/members`] });
      if (mode === 'link' && data.invite_token) {
        const url = `${window.location.origin}/join?token=${encodeURIComponent(data.invite_token)}`;
        setJoinUrl(url);
        toast({
          title: 'Join link ready',
          description: 'Copy the link and send it to this person.',
        });
        return;
      }
      toast({
        title: 'User added',
        description: `${data.email} can sign in with the password you set.`,
      });
      closeModal();
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not add user',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const closeModal = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'COMPANY_MEMBER',
      password: '',
      confirmPassword: '',
    });
    setErrors({});
    setJoinUrl('');
    setMode('password');
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters';
    }

    if (mode === 'password') {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      inviteMutation.mutate(formData);
    }
  };

  const handleChange = (field: keyof InviteMemberData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(joinUrl);
    toast({ title: 'Link copied', description: 'Paste it into an email or message.' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Add team member</h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600"
            disabled={inviteMutation.isPending}
            type="button"
          >
            <i data-feather="x" className="w-5 h-5"></i>
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Adding to <span className="font-medium">{companyName}</span>
        </p>

        {joinUrl ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Send this link to {formData.email}. It works for 7 days. They choose their own password and join {companyName}.
            </p>
            <Input label="Join link" value={joinUrl} readOnly />
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={closeModal}>Done</Button>
              <Button type="button" onClick={copyLink}>Copy link</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('password')}
                className={`rounded-md border px-3 py-2 text-left text-sm ${
                  mode === 'password' ? 'border-green-700 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="font-medium text-gray-900">Set password</div>
                <div className="text-xs text-gray-600">You choose their login</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('link')}
                className={`rounded-md border px-3 py-2 text-left text-sm ${
                  mode === 'link' ? 'border-green-700 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="font-medium text-gray-900">Join link</div>
                <div className="text-xs text-gray-600">They choose a password</div>
              </button>
            </div>

            <div>
              <Input
                label="Email address"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="user@example.com"
                required
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <Input
                label="Full name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder="Jane Doe"
                required
              />
              {errors.full_name && <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="COMPANY_ADMIN"
                    checked={formData.role === 'COMPANY_ADMIN'}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Company admin</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="COMPANY_MEMBER"
                    checked={formData.role === 'COMPANY_MEMBER'}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Company member</span>
                </label>
              </div>
            </div>

            {mode === 'password' && (
              <>
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>
                <div>
                  <Input
                    label="Confirm password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    required
                    minLength={8}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={inviteMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviteMutation.isPending}>
                {inviteMutation.isPending
                  ? 'Saving...'
                  : mode === 'password'
                  ? 'Add user'
                  : 'Create join link'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
